import { useState } from 'react'
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { Button } from '@/components/ui/button'
import { Plus, Search } from 'lucide-react'
import { useProjects } from '@/api/hooks'
import { useMyProjectsFilter } from '@/hooks/use-my-projects-filter'
import { ProjectsTable, ProjectsTableSkeleton } from '@/components/projects-table'
import { DropshipQuoteUploadDialog } from '@/components/dropship-quote-upload-dialog'
import { ProjectFilterFields } from '@/components/project-filters'
import { ListFilters } from '@/components/list-filters'
import { resultCount } from '@/lib/utils'
import { PROJECT_PRIORITY_FILTER_OPTIONS } from '@/lib/project-priority'
import { SERVICE_TIER_FILTER_OPTIONS } from '@/lib/service-tier'
import {
  AUTOMATION_OPTIONS,
  SOURCE_OPTIONS,
  activeProjectFilters,
  projectFilterParams,
} from '@/lib/project-filters'
import { SearchInput } from '@/components/search-input'

export function ProjectsPage() {
  const [search, setSearch] = useUrlSearchText()
  const [filters, setFilters] = useQueryStates({
    status: parseAsString.withDefault('active'),
    stage: parseAsString,
    automation: parseAsStringLiteral(AUTOMATION_OPTIONS).withDefault('all'),
    source: parseAsStringLiteral(SOURCE_OPTIONS).withDefault('all'),
    priority: parseAsStringLiteral(PROJECT_PRIORITY_FILTER_OPTIONS).withDefault('all'),
    tier: parseAsStringLiteral(SERVICE_TIER_FILTER_OPTIONS).withDefault('all'),
    staff: parseAsInteger,
    page: parseAsInteger.withDefault(1),
  })
  const { page } = filters
  const selectedStaffId = filters.staff ?? undefined
  const [dialogOpen, setDialogOpen] = useState(false)

  const { showMyProjects, staffId } = useMyProjectsFilter()
  const { data, isLoading } = useProjects({
    search: search || undefined,
    ...projectFilterParams(filters),
    staff_id: staffId ?? selectedStaffId,
    page,
    limit: 20,
  })

  // Filters set away from their defaults, counted on the Filters button.
  const activeFilters = activeProjectFilters(filters, 'active', showMyProjects)

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold tracking-tight">Projects</h1>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Project
        </Button>
        <DropshipQuoteUploadDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      </div>

      <ListFilters

        count={resultCount(data?.total, 'project')}
        active={activeFilters}
        onClear={() =>
          void setFilters({ status: null, stage: null, automation: null, source: null, priority: null, tier: null, staff: null, page: 1 })
        }
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              placeholder="Search projects..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                void setFilters({ page: 1 })
              }}
              className="pl-9"
            />
          </div>
        }
      >
        <ProjectFilterFields
          values={filters}
          onChange={(change) => void setFilters({ ...change, page: 1 })}
          staffLocked={showMyProjects}
          lockedStaffId={staffId}
        />
      </ListFilters>

      {isLoading ? (
        <ProjectsTableSkeleton />
      ) : data?.items?.length ? (
        <ProjectsTable items={data.items} />
      ) : (
        <div className="text-center text-muted-foreground py-12">No projects found</div>
      )}

      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Page {data?.page ?? 1} of {data?.pages ?? 1} ({data?.total ?? 0} total)
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: Math.max(1, page - 1) })}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: page + 1 })}
            disabled={page >= (data?.pages ?? 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
